import { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { Auth } from "firebase/auth";

import type {
  FlexFormTemplate,
  FormFieldConfig,
  FormFieldCondition,
  FormFieldDependency,
  FormSectionConfig,
} from "@captiva/shared-types";

export interface AuthWithClaims extends Auth {
  claims?: {
    email?: string;
    sub?: string;
    companyId?: string;
    user_id?: string;
    admin?: boolean;
    [key: string]: any;
  };
  currentUser: any; // We can use the firebase User type, but keeping it flexible for mocks
}

/**
 * Remap field IDs and all internal references (displayIf conditions, dependencies, and sections)
 * to newly generated UUIDs.
 */
export function remapTemplateFieldIds(
  fields: FormFieldConfig[] = [],
  sections?: FormSectionConfig[] | null
): {
  clonedFields: FormFieldConfig[];
  clonedSections: FormSectionConfig[] | null | undefined;
  fieldIdMapping: Map<string, string>;
} {
  const fieldIdMapping = new Map<string, string>();

  // 1. Generate new UUIDs for each field and record in mapping
  const clonedFields: FormFieldConfig[] = (fields || []).map((field) => {
    const newFieldId = crypto.randomUUID();
    if (field.id) {
      fieldIdMapping.set(field.id, newFieldId);
    }
    return {
      ...field,
      id: newFieldId,
    };
  });

  // 2. Remap internal field references (displayIf conditions and dependencies)
  const mappedClonedFields = clonedFields.map((field) => {
    const updatedField: FormFieldConfig = { ...field };

    if (Array.isArray(updatedField.displayIf)) {
      updatedField.displayIf = updatedField.displayIf.map((cond: FormFieldCondition) => ({
        ...cond,
        fieldId: fieldIdMapping.get(cond.fieldId) || cond.fieldId,
      }));
    }

    if (Array.isArray(updatedField.dependencies)) {
      updatedField.dependencies = updatedField.dependencies.map((dep: FormFieldDependency) => ({
        ...dep,
        fieldId: fieldIdMapping.get(dep.fieldId) || dep.fieldId,
      }));
    }

    return updatedField;
  });

  // 3. Update section field references (if sections exist)
  const clonedSections = Array.isArray(sections)
    ? sections.map((section: FormSectionConfig) => ({
        ...section,
        fields: (section.fields || []).map((fieldId) => fieldIdMapping.get(fieldId) || fieldId),
      }))
    : sections;

  return {
    clonedFields: mappedClonedFields,
    clonedSections,
    fieldIdMapping,
  };
}

export interface UseDuplicateTemplateResult {
  duplicateTemplate: (template: FlexFormTemplate) => Promise<string>;
  duplicating: boolean;
  error: string | null;
}

export function useDuplicateTemplate(companyId: string, auth: AuthWithClaims): UseDuplicateTemplateResult {
  const [duplicating, setDuplicating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const duplicateTemplate = async (template: FlexFormTemplate): Promise<string> => {
    setDuplicating(true);
    setError(null);
    try {
      const { clonedFields, clonedSections } = remapTemplateFieldIds(
        template.fields,
        template.sections
      );

      // Create a copy of the template
      const duplicatedTemplate = {
        ...template,
        companyId,
        name: `${template.name} (copy)`,
        fields: clonedFields,
        ...(template.sections !== undefined && { sections: clonedSections }),
        archived: false,
        createdBy: auth?.claims?.email || "templates",
        createdAt: serverTimestamp(),
        updatedBy: auth?.claims?.email || "unknown",
        updatedAt: serverTimestamp(),
      };
      delete (duplicatedTemplate as { id?: string }).id; // Let Firestore generate new ID

      // Save the duplicated template
      const docRef = await addDoc(collection(db, "flexFormTemplates"), duplicatedTemplate);
      const newTemplateId = docRef.id;

      // Duplicate the AI story if it exists
      if (template.id) {
        await duplicateAIStory(template.id, newTemplateId, companyId, auth);
      }

      return newTemplateId;
    } catch (err: any) {
      setError(err.message || "Error duplicating template");
      throw err;
    } finally {
      setDuplicating(false);
    }
  };

  const duplicateAIStory = async (
    oldTemplateId: string,
    newTemplateId: string,
    companyId: string,
    auth: AuthWithClaims
  ): Promise<void> => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const idToken = await user.getIdToken();
      const X_API_KEY = process.env.REACT_APP_X_API_KEY;

      // Fetch existing story
      const fetchResponse = await fetch(`https://europe-west3-twodata-dev.cloudfunctions.net/tenantapi/flexFormStoryTemplates/${oldTemplateId}?companyId=${companyId}`, {
        method: 'GET',
        headers: {
          "x-api-key": X_API_KEY || "",
          'Authorization': `Bearer ${idToken}`,
        },
      });
      if (fetchResponse.ok) {
        const data = await fetchResponse.json();
        if (data.handlebarsTemplate) {
          // Save for new template
          await fetch(`https://europe-west3-twodata-dev.cloudfunctions.net/tenantapi/flexFormStoryTemplates/${newTemplateId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              "x-api-key": X_API_KEY || "",
              'Authorization': `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              handlebarsTemplate: data.handlebarsTemplate,
              templateId: newTemplateId,
              companyId,
              active: true,
            }),
          });
        }
      }
    } catch (error) {
      console.error("Error duplicating AI story:", error);
      // Don't fail the whole duplication if story fails
    }
  };

  return {
    duplicateTemplate,
    duplicating,
    error,
  };
}